/* eslint-disable react/button-has-type */
import { type ButtonHTMLAttributes, type CSSProperties, type RefObject } from 'react';
import { getButtonAriaProps } from '@common/components/controls/ariaHelpers/buttonAriaHelpers';
import { type OptionalTestId } from '@common/components/controls/TestId';
import Icon, { type IconProps } from '@common/components/Icon';
import { getColorVariable, type IconColor } from '@common/types/colors';
import classnames from '@common/utils/ui/classnames';
import createRipple from '@common/utils/ui/ripple';
import '@common/styles/components/iconButton.pcss';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconProps['component'];
  iconSize?: 16 | 24;
  color?: IconColor;
  forbidden?: boolean;
  large?: boolean;
  small?: boolean;
  darkBackground?: boolean;
  opacityBackground?: boolean;
  transparentBackground?: boolean;
  errorState?: boolean;
  buttonRef?: RefObject<HTMLButtonElement | null>;
}

function IconButton({
  icon,
  iconSize = 24,
  color = 'regular',
  disabled,
  forbidden,
  large,
  small,
  darkBackground,
  opacityBackground,
  transparentBackground,
  errorState,
  buttonRef,
  onClick,
  type = 'button',
  className = '',
  style,
  'data-testid': testId,
  ...rest
}: OptionalTestId<IconButtonProps>) {
  const ariaProps = getButtonAriaProps({
    disabled,
    forbidden,
    ariaLabel: rest['aria-label'],
  });

  const isDisabled = disabled || forbidden;
  const colorVariable = getColorVariable(color);
  const iconColorStyle = !isDisabled && !errorState && !darkBackground && colorVariable
    ? { ...style, '--local-icon-color': `var(--${colorVariable})` } as CSSProperties
    : style;

  return (
    <button
      ref={buttonRef}
      {...rest}
      {...ariaProps}
      style={iconColorStyle}
      className={classnames({
        'icon-button': true,
        'icon-button--large': large,
        'icon-button--small': small,
        'icon-button--dark-bg': darkBackground,
        'icon-button--opacity-bg': opacityBackground,
        'icon-button--transparent-bg': transparentBackground,
        'icon-button--state-error': errorState,
        'icon-button--forbidden': !disabled && forbidden,
        [className]: !!className,
      })}
      type={type}
      disabled={disabled || forbidden}
      onClick={(event) => {
        if (!buttonRef?.current?.className.includes('ripple')) {
          createRipple(event);
        }
        event.stopPropagation();

        if (typeof onClick === 'function') {
          onClick(event);
        }
      }}
      data-testid={testId || `${icon.displayName}-icon-button`} // NOSONAR:S6606 - Empty testId should use generated name
    >
      <Icon
        component={icon}
        size={iconSize}
        color={!disabled && forbidden ? 'forbidden' : color}
      />
    </button>
  );
}

export default IconButton;
